import React from 'react';
import Guru from '@svg/guru';
import styled from 'styled-components';

import { Button, Modal } from '@common/index';
import { useTheme } from '../../../../styles';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { RequestError } from '@requests/common';
import { GetCollectionsParams, getGuruCollections, GuruLoaderCollection, GuruLoaderItem } from '@requests/loaders';

import {
  Alert,
  Checkbox,
  CircularProgress,
  Collapse,
  Divider,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  alpha,
} from '@mui/material';

import {
  DescriptionOutlined,
  FolderOutlined,
  KeyboardArrowDownOutlined,
  KeyboardArrowUpOutlined,
} from '@mui/icons-material';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  border-radius: 6px;

  p {
    color: ${({ theme }) => alpha(theme.text.standard, 0.6)};
    font-size: 14px;
  }

  gap: 1.5rem;

  & .MuiListItemText-secondary {
    max-width: 97%;
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;
  }

  & .MuiListItemIcon-root {
    min-width: 40px;
  }

  & .MuiFormControlLabel-label {
    margin-right: 16px;
  }
`;

const ListContainer = styled.div`
  display: flex;
  width: 97%;
`;

type CollectionFolderProps = {
  item: GuruLoaderItem;
  selectedFolders: GuruLoaderItem[];
  setSelectedFolders: React.Dispatch<React.SetStateAction<GuruLoaderItem[]>>;
};

const CollectionFolderItem: React.FC<CollectionFolderProps> = ({ item, selectedFolders, setSelectedFolders }) => {
  const checked = selectedFolders.some((sel) => sel.id === item.id);

  const handleCheckboxToggle = () => {
    if (selectedFolders.some((sel) => sel.id === item.id))
      setSelectedFolders(selectedFolders.filter((sel) => sel.id !== item.id));
    else setSelectedFolders(selectedFolders.concat(item));
  };

  if (item.type === 'card') {
    return (
      <>
        <ListItem disablePadding sx={{ marginBottom: '4px' }}>
          <ListItemButton
            onClick={() => handleCheckboxToggle()}
            dense
            sx={{ borderRadius: '8px', ml: 4, minHeight: '60px' }}
          >
            <ListItemIcon>
              {checked ? <Checkbox edge='start' checked /> : <DescriptionOutlined sx={{ fontSize: '2.5ch' }} />}
            </ListItemIcon>
            <ListItemText primary={item.preferredPhrase} />
          </ListItemButton>
        </ListItem>
        <Divider component='li' sx={{ marginBottom: '4px', marginLeft: '32px' }} />
      </>
    );
  }

  if (item.type === 'folder') {
    return (
      <>
        <ListItem disablePadding sx={{ marginBottom: '4px' }}>
          <ListItemButton
            onClick={() => handleCheckboxToggle()}
            dense
            disabled={item.numberOfFacts === 0}
            sx={{ borderRadius: '8px', ml: 4, minHeight: '60px' }}
          >
            <ListItemIcon>{checked ? <Checkbox edge='start' checked /> : <FolderOutlined />}</ListItemIcon>
            <ListItemText
              primary={item.title}
              secondary={item?.numberOfFacts ? `${item.numberOfFacts} facts` : `This folder is empty.`}
            />
          </ListItemButton>
        </ListItem>
        <Divider component='li' sx={{ marginBottom: '4px', marginLeft: '32px' }} />
      </>
    );
  }

  return null;
};

type CollectionProps = {
  collection: GuruLoaderCollection;
  selectedCollections: GuruLoaderCollection[];
  setSelectedCollections: React.Dispatch<React.SetStateAction<GuruLoaderCollection[]>>;
  selectedFolders: GuruLoaderItem[];
  setSelectedFolders: React.Dispatch<React.SetStateAction<GuruLoaderItem[]>>;
};

const CollectionListItem: React.FC<CollectionProps> = ({
  collection,
  selectedCollections,
  setSelectedCollections,
  selectedFolders,
  setSelectedFolders,
}) => {
  const checked = selectedCollections.some((sel) => sel.id === collection.id);
  const handleCheckboxToggle = () => {
    if (selectedCollections.some((sel) => sel.id === collection.id))
      setSelectedCollections(selectedCollections.filter((sel) => sel.id !== collection.id));
    else setSelectedCollections(selectedCollections.concat(collection));
  };

  return (
    <>
      <ListItem disablePadding sx={{ marginBottom: '4px' }}>
        <ListItemButton onClick={() => handleCheckboxToggle()} dense sx={{ borderRadius: '8px' }}>
          <ListItemIcon>{checked ? <KeyboardArrowUpOutlined /> : <KeyboardArrowDownOutlined />}</ListItemIcon>
          <ListItemText primary={collection.name} secondary={`${collection.counts.cards} cards`} />
        </ListItemButton>
      </ListItem>
      <Collapse in={checked} timeout='auto' unmountOnExit>
        <List>
          {collection.homeFolders.map((item) => (
            <CollectionFolderItem
              key={item.id}
              item={item}
              selectedFolders={selectedFolders}
              setSelectedFolders={setSelectedFolders}
            />
          ))}
        </List>
      </Collapse>
    </>
  );
};

interface Props {
  open: string | null;
  page: number;
  setOpen: React.Dispatch<string | null>;
  setPage: React.Dispatch<number>;
  reset: () => void;
  configuration: {
    secretIdentifier: string;
    selectedCollections: GuruLoaderCollection[];
    setSelectedCollections: React.Dispatch<React.SetStateAction<GuruLoaderCollection[]>>;
    selectedFolders: GuruLoaderItem[];
    setSelectedFolders: React.Dispatch<React.SetStateAction<GuruLoaderItem[]>>;
  };
}

export const GuruCollections: React.FC<Props> = ({ open, page, setPage, configuration, reset }) => {
  const theme = useTheme();

  const { service_id } = useParams();
  const { secretIdentifier, selectedCollections, setSelectedCollections, selectedFolders, setSelectedFolders } =
    configuration;

  const {
    data: collections = [],
    isLoading: loading,
    error,
  } = useQuery<GuruLoaderCollection[], RequestError, GuruLoaderCollection[], [string, GetCollectionsParams]>({
    queryKey: ['guru-collections', { secret_identifier: secretIdentifier, service_id: service_id as string }],
    queryFn: getGuruCollections,
    enabled: Boolean(service_id),
  });

  if (error) {
    return (
      <Modal
        open={open === 'guru' && page === 2}
        setOpen={reset}
        page={page + 1}
        total_pages={4}
        background={alpha(theme.text.standard, 0.06)}
        header='Guru Error'
        icon={<Guru height={30} color='#000' />}
        body={<Alert severity='error'>{error?.response?.data?.error || error?.message}</Alert>}
        actions={
          <Stack direction='row' spacing={1}>
            <Button onClick={reset}>Cancel</Button>
            <Button customColor='red' onClick={() => setPage(0)}>
              Change Credentials
            </Button>
          </Stack>
        }
      />
    );
  }

  const body = (
    <Container>
      <p>
        Below is a list of collections available in our Guru account. Select the cards and folders you want to load from
        each of the collections below. It is not recommended to load all collections into a single bucket.
      </p>
      {loading && (
        <Stack spacing={1.5} padding='4rem 0rem' width='100%' display='flex' alignItems='center'>
          <p>Loading Guru Collections</p>
          <CircularProgress size={16} sx={{ color: theme.primary }} />
        </Stack>
      )}
      {!loading && (
        <ListContainer>
          <List sx={{ width: '100%' }}>
            {collections.map((collection) => (
              <CollectionListItem
                key={collection.id}
                collection={collection}
                selectedCollections={selectedCollections}
                setSelectedCollections={setSelectedCollections}
                selectedFolders={selectedFolders}
                setSelectedFolders={setSelectedFolders}
              />
            ))}
          </List>
        </ListContainer>
      )}
    </Container>
  );

  const actions = (
    <Stack spacing={1} direction='row'>
      <Button onClick={() => setPage(1)}>Back</Button>
      <Button disabled={selectedFolders.length === 0} customColor='primary' onClick={() => setPage(3)}>
        {selectedFolders.length === 0 ? 'Load Categories' : `Load Categories (${selectedFolders.length})`}
      </Button>
    </Stack>
  );

  return (
    <Modal
      open={open === 'guru' && page === 2}
      setOpen={reset}
      page={page + 1}
      total_pages={4}
      background={alpha(theme.text.standard, 0.06)}
      header='Guru'
      icon={<Guru height={30} color='#000' />}
      body={body}
      actions={actions}
    />
  );
};
